import type { ICreateRecurringTaskCmd } from "@/data/commands/system/CreateRecurringTaskCmd";
import { Factory } from "@/data/dao/Factory";
import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
import { SystemDao } from "@/data/dao/service/SystemDao";
import type { IRecurringTask, ITaskHandler } from "@/data/model/RecurringTask";

import { logError } from "@/shared/logging/errors";

import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { TimePicker } from "@/portal/formControls/TimePicker";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import { DymamicFormBuilder } from "@/portal/forms/DymamicFormBuilder";

export class TaskForm extends BaseEntityForm<IRecurringTask, ICreateRecurringTaskCmd> {
    private $configSection: JQuery;
    private $configInner: JQuery;
    private configForm: DymamicFormBuilder;

    private activeSwitch: Switch;

    private intervalField: TimePicker;
    private handlerField: Select<ITaskHandler>;
    private model?: IRecurringTask;
    private loadedHandlerInfo = false;

    constructor(element: JQuery) {
        super(element, Factory.getInstance<SystemDao>(SystemDao).taskDataSource);

        this.$configSection = this.$form.find("#cfgsection");
        this.$configInner = $('<div class="inner" />').appendTo(this.$configSection);
        this.configForm = new DymamicFormBuilder(this.$configInner);
        this.activeSwitch = new Switch(this.$form.find('input[name="isactive"]'));

        this.handlerField = Select.createFromSelect(
            this.$form.find('.form-control[name="handler"]'),
            this.loadHandler(),
            (f) => f.name,
            true,
            undefined,
            "handler"
        );
        this.handlerField.onChange = () => {
            const m = this.handlerField.getModel();
            this.updateName(m === undefined ? "" : m.name);

            this.updateConfigRows();
        };

        this.intervalField = TimePicker.createFromInput(this.$form.find('.form-control[name="interval"]'), {
            maxHours: 24 * 5,
        });

        this.intervalField.getValue();
    }

    public setFormData(model?: IRecurringTask) {
        this.model = model;
        this.isReady = false;

        this.updateName(model === undefined ? "" : model.name);
        this.activeSwitch.setValue(model === undefined || model === undefined ? false : model.isActive);
        this.intervalField.setValue(model === undefined ? undefined : model.interval);
        this.handlerField.setValue(model === undefined ? undefined : model.handler);
        this.handlerField.setDisabled(model !== undefined);

        if (this.loadedHandlerInfo === false) {
            this.loadedHandlerInfo = true;
            this.handlerField.fetchData(() => {
                this.updateConfigRows();
                this.isReady = true;
            });
        } else {
            this.isReady = true;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateRecurringTaskCmd {
        const cfg = this.configForm.validateAndGet();

        const data = {
            isActive: this.activeSwitch.getValue(),
            handler: this.handlerField.getValue(),
            interval: this.intervalField.getValue(),
            config: cfg.values,
        };

        let hasError: boolean = cfg.hasError;

        if (this.handlerField.getValue() === undefined) {
            hasError = true;
            this.handlerField.setError(true);
        } else this.handlerField.setError(false);

        if (this.intervalField.getSeconds()! < 15) {
            hasError = true;
            this.intervalField.setError(true);
        } else this.intervalField.setError(false);

        return (hasError ? undefined : data) as any;
    }

    private loadHandler(): IDataSource<ITaskHandler> {
        return {
            getData: () => {
                return new Promise((resolve, reject) => {
                    return Factory.getInstance<SystemDao>(SystemDao)
                        .getTaskHandlers({
                            forHandler: this.model === undefined ? undefined : this.model.handler,
                        })
                        .then((a) => {
                            resolve({
                                limit: 0,
                                offset: 0,
                                hasNext: false,
                                hasPrev: false,
                                items: a,
                            });
                        })
                        .catch((ex) => {
                            logError(ex);
                            reject();
                        });
                });
            },
        };
    }

    private updateConfigRows() {
        const handler = this.handlerField.getModel();
        if (handler === undefined || handler.configSchema.length === 0) {
            this.$configSection.hide();
            return;
        }

        this.$configSection.show();

        this.configForm.buildBySchema(handler.configSchema);
        if (this.model !== undefined) this.configForm.setValues(this.model.config);
    }
}
